import React from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/usePermissions";
import { api } from "@/lib/api";
import { getQuoteFieldLabel, isCompanyQuoteField, isIssueAgeQuoteField, isProductQuoteField } from "@/lib/quoteFieldUtils";

export default function AddQuoteDialog({
  open,
  onOpenChange,
  saveSelectTriggerClass,

  selectedQuoteProspectId,
  setSelectedQuoteProspectId,
  quoteProspectOptions,

  quoteTemplateOption,
  setQuoteTemplateOption,
  quoteTemplates,

  quoteTemplateLoading,
  selectedTemplate,
  quoteTemplateFields,

  setQuoteTemplates,

  navigate,
  location,

  handleAddQuoteTemplate,
}) {
  const hasMortgageTemplate = React.useMemo(
    () => (quoteTemplates || []).some((t) => String(t?.name || "").trim().toLowerCase() === "mortgage protection"),
    [quoteTemplates]
  );
  const { toast } = useToast();
  // P4 Objective B: template MANAGEMENT authority, projected from the canonical
  // selected-Account context rather than the deprecated `users.role`.
  //
  // This gates the Edit Template affordance, and through it the whole edit
  // dialog -- rename, field editing, Save (PUT) and Delete (DELETE). It
  // therefore has to project exactly what the backend enforces on those two
  // routes (routers/quote_templates.py): RequireAnyModule over
  // clients|appointments|playbooks at "full", AND PermissionContext
  // .is_account_admin. Both halves are required; either alone is a different,
  // wider rule.
  //
  // `userRole` is the SAME ctx.role the backend derives -- one shared
  // effective-role projection since PR #577 -- so this is correct for a direct
  // Owner/Admin, for an inherited Agency Owner/Admin operating in a child
  // Account (inherited role, no membership row), and for a Platform SuperAdmin
  // (projected "owner"). It needs no special case for any of them.
  //
  // `users.role` is deliberately NOT consulted. It is written once at
  // registration and never refreshed on Account switch, so it can disagree with
  // the caller's authority in the Account they are actually operating in.
  //
  // `isResolvedForSelectedAccount` (P4-WP-9A.2a) is the fail-closed conjunct: it
  // is false while authorization is unresolved, when the context belongs to a
  // previously selected Account, and when the endpoint refused outright -- an
  // inactive direct Membership being the case that matters here. Without it an
  // empty `moduleAccessLevels` would be indistinguishable from a denial that has
  // actually been decided.
  const { userRole, moduleAccessLevels, isResolvedForSelectedAccount } = usePermissions();
  const isAccountAdmin = ["owner", "admin"].includes(userRole);
  const hasFullOnAny = ["clients", "appointments", "playbooks"].some(
    (m) => moduleAccessLevels[m] === "full"
  );
  const canManageTemplates = isResolvedForSelectedAccount && isAccountAdmin && hasFullOnAny;
  const [editTemplateOpen, setEditTemplateOpen] = React.useState(false);
  const [templateDraft, setTemplateDraft] = React.useState({ name: "", fields: [] });
  const [templateSaving, setTemplateSaving] = React.useState(false);
  const selectedTemplateId = React.useMemo(() => {
    if (!quoteTemplateOption) return "";
    if (quoteTemplateOption.startsWith("template:")) {
      return quoteTemplateOption.replace("template:", "");
    }
    return "";
  }, [quoteTemplateOption]);

  const selectedTemplateDetails = React.useMemo(
    () => (quoteTemplates || []).find((template) => template.id === selectedTemplateId) || null,
    [quoteTemplates, selectedTemplateId]
  );

  const handleOpenEditTemplate = () => {
    if (!selectedTemplateDetails) return;
    setTemplateDraft({
      name: selectedTemplateDetails.name || "",
      fields: (selectedTemplateDetails.fields || []).map((field) => ({
        id: field.id,
        label: field.label || "",
        field_type: field.field_type || "text",
        dropdown_options: field.dropdown_options || [],
      })),
    });
    setEditTemplateOpen(true);
  };

  const handleUpdateTemplateField = (index, updates) => {
    setTemplateDraft((prev) => {
      const fields = [...prev.fields];
      fields[index] = { ...fields[index], ...updates };
      return { ...prev, fields };
    });
  };

  const handleAddTemplateField = () => {
    setTemplateDraft((prev) => ({
      ...prev,
      fields: [
        ...prev.fields,
        {
          id: crypto.randomUUID(),
          label: "",
          field_type: "text",
          dropdown_options: [],
        },
      ],
    }));
  };

  const handleRemoveTemplateField = (index) => {
    setTemplateDraft((prev) => ({
      ...prev,
      fields: prev.fields.filter((_, fieldIndex) => fieldIndex !== index),
    }));
  };

  const handleMoveTemplateField = (index, direction) => {
    setTemplateDraft((prev) => {
      const fields = [...prev.fields];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= fields.length) return prev;
      const [moved] = fields.splice(index, 1);
      fields.splice(targetIndex, 0, moved);
      return { ...prev, fields };
    });
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplateDetails) return;
    if (!templateDraft.name.trim()) {
      toast({ title: "Template name is required.", variant: "destructive" });
      return;
    }
    setTemplateSaving(true);
    try {
      const orderedFields = templateDraft.fields.map((field) => ({
        id: field.id,
        label: field.label?.trim() || "Untitled",
        field_type: field.field_type || "text",
        dropdown_options: field.dropdown_options || [],
      }));

      const withRequired = (() => {
        const list = [...orderedFields];
        const hasCompany = list.some((f) => isCompanyQuoteField(getQuoteFieldLabel(f)));
        const hasProduct = list.some((f) => isProductQuoteField(getQuoteFieldLabel(f)));
        const hasIssueAge = list.some((f) => isIssueAgeQuoteField(getQuoteFieldLabel(f)));
        if (!hasCompany) {
          list.unshift({ id: "company", label: "Company", field_type: "dropdown", dropdown_options: [] });
        }
        if (!hasProduct) {
          list.push({ id: "product", label: "Product", field_type: "dropdown", dropdown_options: [] });
        }
        if (!hasIssueAge) {
          const issueAgeField = { id: "issue_age", label: "Issue Age", field_type: "dropdown", dropdown_options: [] };
          const productIndex = list.findIndex((field) => isProductQuoteField(getQuoteFieldLabel(field)));
          if (productIndex >= 0) {
            list.splice(productIndex + 1, 0, issueAgeField);
          } else {
            list.push(issueAgeField);
          }
        }
        return list;
      })();

      const payload = {
        name: templateDraft.name.trim(),
        option_count: selectedTemplateDetails.option_count || 3,
        fields: withRequired,
      };
      const { data } = await api.put(`/quote-templates/${selectedTemplateDetails.id}`, payload);
      if (setQuoteTemplates) {
        setQuoteTemplates((prev) =>
          (prev || []).map((template) => (template.id === selectedTemplateDetails.id ? data : template))
        );
      }
      setEditTemplateOpen(false);
      toast({ title: "Template updated." });
    } catch (error) {
      toast({ title: "Unable to update template.", variant: "destructive" });
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplateDetails) return;
    const confirmed = window.confirm(`Delete template "${selectedTemplateDetails.name}"? This cannot be undone.`);
    if (!confirmed) return;
    try {
      await api.delete(`/quote-templates/${selectedTemplateDetails.id}`);
      if (setQuoteTemplates) {
        setQuoteTemplates((prev) => (prev || []).filter((template) => template.id !== selectedTemplateDetails.id));
      }
      setQuoteTemplateOption("none");
      setEditTemplateOpen(false);
      toast({ title: "Template deleted." });
    } catch (error) {
      toast({ title: "Unable to delete template.", variant: "destructive" });
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl bg-slate-950 text-slate-100 ring-1 ring-white/10"
        data-testid="quote-template-dialog"
      >
        <DialogHeader>
          <DialogTitle>Add Quote</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-200/80">Prospect</Label>
            <Select value={selectedQuoteProspectId} onValueChange={setSelectedQuoteProspectId}>
              <SelectTrigger className={saveSelectTriggerClass} data-testid="add-quote-prospect-select-trigger">
                <SelectValue placeholder="Select a prospect" />
              </SelectTrigger>
              <SelectContent className="glass border-white/10">
                {quoteProspectOptions.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-200/80">Quote Type</Label>
            <Select value={quoteTemplateOption} onValueChange={setQuoteTemplateOption}>
              <SelectTrigger className={saveSelectTriggerClass} data-testid="add-quote-template-select-trigger">
                <SelectValue placeholder="Select a quote type" />
              </SelectTrigger>
              <SelectContent className="glass border-white/10">
              {!hasMortgageTemplate ? <SelectItem value="mortgage-protection">Mortgage Protection</SelectItem> : null}
                {(quoteTemplates || []).map((t) => (
                  <SelectItem key={t.id} value={`template:${t.id}`}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {quoteTemplateLoading ? (
            <div
              className="rounded-xl bg-white/5 px-3 py-2 text-xs text-slate-200/70"
              data-testid="quote-template-fields-preview"
            >
              Loading template fields...
            </div>
          ) : null}

          {(selectedTemplate?.fields || quoteTemplateFields).length ? (
            <div className="relative">
              <div className="max-h-40 space-y-1 overflow-y-auto rounded-xl bg-white/5 px-3 py-6 text-xs text-slate-200/80 ring-1 ring-white/10">
                {(selectedTemplate?.fields || quoteTemplateFields).map((field) => (
                  <div key={field.id || field.label} className="truncate">
                    {field.label}
                  </div>
                ))}
              </div>
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 rounded-b-xl bg-gradient-to-t from-slate-950/90 to-transparent" />
            </div>
          ) : null}
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              className="rounded-full bg-indigo-500/20 text-indigo-100 ring-1 ring-indigo-400/20 hover:bg-indigo-500/30"
              onClick={() =>
                navigate(`/app/quote-templates/new?returnTo=${encodeURIComponent(location.pathname + location.search)}`)
              }
              data-testid="quote-template-create-template-button"
            >
              Create Template
            </Button>
            {canManageTemplates && selectedTemplateDetails && (
              <Button
                type="button"
                variant="secondary"
                className="rounded-full bg-white/10 text-slate-100 hover:bg-white/15"
                onClick={handleOpenEditTemplate}
                data-testid="quote-template-edit-template-button"
              >
                Edit Template
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              className="rounded-full bg-white/10 text-slate-100 hover:bg-white/15"
              onClick={() => onOpenChange(false)}
              data-testid="quote-template-cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="rounded-full bg-indigo-500/20 text-indigo-100 ring-1 ring-indigo-400/20 hover:bg-indigo-500/30"
              onClick={handleAddQuoteTemplate}
              disabled={quoteTemplateLoading}
              data-testid="quote-template-add-button"
            >
              Add Quote
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
      </Dialog>
      <Dialog open={editTemplateOpen} onOpenChange={setEditTemplateOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto bg-slate-950 text-slate-100">
          <DialogHeader>
            <DialogTitle>Edit Quote Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input
                value={templateDraft.name}
                onChange={(event) => setTemplateDraft((prev) => ({ ...prev, name: event.target.value }))}
                data-testid="quote-template-edit-name-input"
              />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Fields</Label>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleAddTemplateField}
                  data-testid="quote-template-edit-add-field-button"
                >
                  Add Field
                </Button>
              </div>
              <div className="space-y-3">
                {templateDraft.fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="flex flex-wrap items-center gap-2 rounded-lg border border-white/10 bg-white/5 p-3"
                    data-testid={`quote-template-edit-field-${index}`}
                  >
                    <Input
                      value={field.label}
                      onChange={(event) => handleUpdateTemplateField(index, { label: event.target.value })}
                      placeholder="Field label"
                      className="flex-1 min-w-[200px]"
                      data-testid={`quote-template-edit-field-label-${index}`}
                    />
                    <Select
                      value={field.field_type}
                      onValueChange={(value) => handleUpdateTemplateField(index, { field_type: value })}
                    >
                      <SelectTrigger className="w-[160px]" data-testid={`quote-template-edit-field-type-${index}`}
                      >
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Text</SelectItem>
                        <SelectItem value="number">Number</SelectItem>
                        <SelectItem value="dropdown">Dropdown</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveTemplateField(index, -1)}
                        data-testid={`quote-template-edit-field-up-${index}`}
                      >
                        Up
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveTemplateField(index, 1)}
                        data-testid={`quote-template-edit-field-down-${index}`}
                      >
                        Down
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => handleRemoveTemplateField(index)}
                        data-testid={`quote-template-edit-field-remove-${index}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            {selectedTemplateDetails && (
              <Button
                type="button"
                variant="destructive"
                onClick={handleDeleteTemplate}
                data-testid="quote-template-edit-delete-button"
              >
                Delete Template
              </Button>
            )}
            <Button type="button" variant="ghost" onClick={() => setEditTemplateOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSaveTemplate}
              disabled={templateSaving}
              data-testid="quote-template-edit-save-button"
            >
              {templateSaving ? "Saving..." : "Save Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
